import React, { useEffect, useState } from 'react';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import LicenseGate from '../components/ui/LicenseGate';
import DashboardSectionAnchor from '../components/ui/DashboardSectionAnchor';
import { ChartGuideModal, ChartInfoButton, FavoriteButton } from '../components/ui';
import { useSectionHashTabSwitch } from '../hooks/useSectionHashTabSwitch';
import apiClient from '../utils/apiClient';
import { TimePeriod } from '../types/api';
import { logger } from '../utils/logger';
import { CHART_GUIDES } from '../constants/chartGuides';
import useChartGuide from '../hooks/useChartGuide';
import { useFavoriteToggle } from '../hooks/useFavoriteToggle';
import {
  OverallSubscription,
  ShiftSubscription,
  OrderTypeSubscription
} from '../components/subscription';

interface SubscriptionDashboardProps {
  branchId?: number;
  timePeriod?: string | number;
  compact?: boolean;
}

type TabType = 'overview' | 'shifts' | 'orderTypes';

const SUBSCRIPTION_SECTION_TABS: Record<string, TabType> = {
  'overall-subscription': 'overview',
  'shift-subscription': 'shifts',
  'order-type-subscription': 'orderTypes',
};

const SubscriptionDashboard: React.FC<SubscriptionDashboardProps> = ({
  branchId = 0,
  timePeriod,
  compact = false
}) => {
  const { timeFrame } = useTimeFrame();
  const { isModalOpen, currentGuide, openGuide, closeGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const [selectedBranch, setSelectedBranch] = useState<number>(branchId);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  useSectionHashTabSwitch(SUBSCRIPTION_SECTION_TABS, setActiveTab);

  useEffect(() => {
    setSelectedBranch(branchId);
  }, [branchId]);

  const getTimePeriodNumber = (period: string | number): number => {
    if (typeof period === 'number') return period;

    switch (period) {
      case 'last1Month': return 1;
      case 'last3Months': return 3;
      case 'last6Months': return 6;
      case 'lastYear':
      case 'last12Months': return 12;
      case 'last24Months': return 24;
      default: return 6;
    }
  };

  const getTimePeriodType = (period: string | number): TimePeriod => {
    const months = typeof period === 'number' ? period : getTimePeriodNumber(period);
    if (months <= 1) return '1month';
    if (months <= 3) return '3months';
    if (months <= 6) return '6months';
    return '12months';
  };

  const effectiveTimePeriod = timePeriod || timeFrame;
  const timeRangeForComponents = getTimePeriodType(effectiveTimePeriod);

  useEffect(() => {
    const fetchBranchInfo = async () => {
      setLoading(true);
      setError(null);

      try {
        await apiClient.get(`/branches/${selectedBranch}`);
      } catch (err) {
        logger.error('Error fetching branch info', 'SubscriptionDashboard', err);
        setError('خطا در بارگذاری اطلاعات شعبه');
      } finally {
        setLoading(false);
      }
    };

    if (selectedBranch) {
      fetchBranchInfo();
    }
  }, [selectedBranch]);

  const handleComponentError = (error: Error) => {
    logger.error('Component error', 'SubscriptionDashboard', error);
  };

  const tabButtonClass = (tab: TabType, extraClass = '') => `
    relative flex items-center justify-center gap-2 px-4 sm:px-8 py-2.5 text-sm sm:text-base font-medium transition-all duration-300 flex-shrink-0 ${extraClass}
    ${activeTab === tab
      ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 border-t-2 border-r-2 border-l-2 border-border-secondary rounded-t-xl shadow-[0_-8px_16px_rgba(0,0,0,0.15),-6px_0_12px_rgba(0,0,0,0.08),6px_0_12px_rgba(0,0,0,0.08)]'
      : 'bg-gray-50 dark:bg-gray-900 text-text-secondary hover:text-text-primary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-xl'}
  `;

  return (
    <LicenseGate licenseType="club-or-evaluation" dashboardName="اشتراک‌گیری">
      <div className="container-dashboard" dir="rtl">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {loading ? (
            <div className="flex flex-col items-center justify-center min-h-[300px]" role="status" aria-live="polite">
              <LoadingSpinner
                variant="minimal"
                size="lg"
                message="در حال بارگذاری داده های داشبورد…"
              />
              <p className="mt-4 text-sm content-secondary">لطفاً چند لحظه صبر کنید…</p>
            </div>
          ) : error ? (
            <div className="p-4 m-6 bg-status-error text-text-inverse rounded-lg shadow-md animate-enter" role="alert" aria-live="assertive">
              <div className="flex items-center">
                <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">{error}</span>
              </div>
            </div>
          ) : (
            <>
              {/* <div className="flex flex-wrap items-center justify-between gap-3"> */}
                <h1 className="title-dashboard">اشتراک‌گیری</h1>
                {/* {!compact && (
                  <div className="flex items-center gap-2">
                    <ChartInfoButton onClick={() => openGuide(CHART_GUIDES.subscriptionDashboard)} />
                    <FavoriteButton
                      componentId="subscription-dashboard"
                      isFavorited={isFavorited('subscription-dashboard')}
                      onClick={() => handleFavoriteToggle({
                        componentId: 'subscription-dashboard',
                        componentTitle: 'داشبورد اشتراک‌گیری',
                        dashboardSource: 'subscription',
                        componentType: 'mixed',
                        description: 'داشبورد کامل اشتراک‌گیری شامل نمای کلی، شیفت کاری و نوع سفارش',
                        props: { branchId: selectedBranch, timePeriod: effectiveTimePeriod, compact: true },
                        gridLayout: { containerClass: 'card-base card-padding' }
                      })}
                    />
                  </div>
                )} */}
              {/* </div> */}

              <div className="card-base card-padding animate-enter animate-delay-3">
                <div className="border-b border-border-secondary">
                  <div className="flex mobile-tab-scroll lg:flex-wrap items-stretch lg:items-center justify-start lg:justify-center -mb-px w-full max-w-full">
                    <button onClick={() => setActiveTab('overview')} className={tabButtonClass('overview')}>
                      <span className="text-lg font-semibold">نمای کلی</span>
                      {activeTab === 'overview' && <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800"></div>}
                    </button>
                    <button onClick={() => setActiveTab('shifts')} className={tabButtonClass('shifts', 'mr-2')}>
                      <span className="text-lg font-semibold">شیفت ها</span>
                      {activeTab === 'shifts' && <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800"></div>}
                    </button>
                    <button onClick={() => setActiveTab('orderTypes')} className={tabButtonClass('orderTypes', 'mr-2')}>
                      <span className="text-lg font-semibold">نوع سفارش</span>
                      {activeTab === 'orderTypes' && <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800"></div>}
                    </button>
                  </div>
                </div>

                <div className="animate-enter">
                  {activeTab === 'overview' && (
                    <DashboardSectionAnchor sectionId="overall-subscription">
                      <OverallSubscription
                        branchId={selectedBranch}
                        timeRange={timeRangeForComponents}
                        onError={handleComponentError}
                      />
                    </DashboardSectionAnchor>
                  )}

                  {activeTab === 'shifts' && (
                    <DashboardSectionAnchor sectionId="shift-subscription">
                      <ShiftSubscription
                        branchId={selectedBranch}
                        timeRange={timeRangeForComponents}
                        onError={handleComponentError}
                      />
                    </DashboardSectionAnchor>
                  )}

                  {activeTab === 'orderTypes' && (
                    <DashboardSectionAnchor sectionId="order-type-subscription">
                      <OrderTypeSubscription
                        branchId={selectedBranch}
                        timeRange={timeRangeForComponents}
                        onError={handleComponentError}
                      />
                    </DashboardSectionAnchor>
                  )}
                </div>
              </div>

              {currentGuide && !compact && (
                <ChartGuideModal
                  isOpen={isModalOpen}
                  onClose={closeGuide}
                  title={currentGuide.title}
                >
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">توضیحات</h3>
                      <p className="text-text-secondary">{currentGuide.description}</p>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نحوه تفسیر</h3>
                      <p className="text-text-secondary">{currentGuide.interpretation}</p>
                    </div>
                    {currentGuide.tips.length > 0 && (
                      <div>
                        <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نکات کلیدی</h3>
                        <ul className="list-disc list-inside space-y-2">
                          {currentGuide.tips.map((tip, index) => (
                            <li key={index} className="text-text-secondary">{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </ChartGuideModal>
              )}
            </>
          )}
        </div>
      </div>
    </LicenseGate>
  );
};

export default SubscriptionDashboard;
